<template>
  <div>
    <div class="d-flex justify-content-between align-items-center flex-wrap mb-1">
      <div>
        <h4 class="mb-0">Partner API usage</h4>
        <small class="text-muted">
          {{ windowSummary }}
        </small>
      </div>

      <div class="d-flex align-items-center">
        <usage-window-picker
          v-model="preset"
          :disabled="loading"
          class="mr-1"
          @refresh="fetch"
        />
        <b-button size="sm" variant="outline-secondary" :to="{ name: 'api-clients' }">
          Clients
        </b-button>
      </div>
    </div>

    <b-overlay :show="loading" rounded="sm">
      <usage-anomalies :anomalies="anomalies" />

      <usage-totals :totals="totals" />

      <b-card no-body class="border mb-0">
        <b-card-body class="pb-0">
          <h5 class="mb-0">Clients</h5>
          <small class="text-muted">
            Busiest first. Unattributed rows are calls whose credentials never resolved to a client.
          </small>
        </b-card-body>

        <b-table
          :items="clients"
          :fields="fields"
          responsive
          show-empty
          empty-text="No partner traffic in this window."
          class="mb-0 usage-clients-table"
        >
          <template #cell(title)="data">
            <router-link
              v-if="data.item.client_id"
              :to="{ name: 'api-clients-usage-view', params: { id: data.item.client_id } }"
              class="font-weight-bold"
            >
              {{ data.item.title || 'Untitled client' }}
            </router-link>
            <span v-else class="font-weight-bold text-muted">Unattributed</span>

            <div class="small text-muted">
              {{ data.item.client_key || '—' }}
            </div>
          </template>

          <template #cell(status)="data">
            <b-badge
              v-if="data.item.status"
              pill
              :variant="`light-${statusBadge(data.item.status)}`"
              class="text-capitalize"
            >
              {{ data.item.status }}
            </b-badge>
            <span v-else class="text-muted">—</span>
          </template>

          <template #cell(requests)="data">
            {{ formatCount(data.item.requests) }}
          </template>

          <template #cell(error_rate)="data">
            <b-badge pill :variant="`light-${errorRateVariant(data.item.error_rate)}`">
              {{ formatPercent(data.item.error_rate, 1) }}
            </b-badge>
          </template>

          <template #cell(p95_ms)="data">
            {{ formatDuration(data.item.p95_ms) }}
          </template>

          <template #cell(rows_served)="data">
            {{ formatCount(data.item.rows_served) }}
          </template>

          <template #cell(response_bytes)="data">
            {{ formatBytes(data.item.response_bytes) }}
          </template>

          <template #cell(actions)="data">
            <b-button
              v-if="data.item.client_id"
              size="sm"
              variant="flat-primary"
              :to="{ name: 'api-clients-usage-view', params: { id: data.item.client_id } }"
            >
              Details
            </b-button>
          </template>
        </b-table>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import { get } from 'lodash'
import {
  BCard,
  BCardBody,
  BTable,
  BBadge,
  BButton,
  BOverlay,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import UsageTotals from '@core/components/api-clients/usage/UsageTotals.vue'
import UsageAnomalies from '@core/components/api-clients/usage/UsageAnomalies.vue'
import UsageWindowPicker from '@core/components/api-clients/usage/UsageWindowPicker.vue'
import {
  errorRateVariant,
  formatBytes,
  formatCount,
  formatDuration,
  formatPercent,
  windowParams,
  WINDOW_PRESETS,
} from '@core/components/api-clients/usage/usageFormat'

/**
 * Cross-client usage overview.
 *
 * Every figure here comes from pre-aggregated rollups, so widening the window from a day
 * to ninety days costs essentially the same query — which is why the range picker offers
 * long windows without hedging.
 */
export default {
  components: {
    BCard,
    BCardBody,
    BTable,
    BBadge,
    BButton,
    BOverlay,
    UsageTotals,
    UsageAnomalies,
    UsageWindowPicker,
  },
  data() {
    return {
      loading: false,
      preset: WINDOW_PRESETS[0],

      totals: {},
      clients: [],
      anomalies: [],
      resolvedWindow: null,

      fields: [
        { key: 'title', label: 'Client' },
        { key: 'status', label: 'Status' },
        { key: 'requests', label: 'Calls', class: 'text-right' },
        { key: 'error_rate', label: 'Errors', class: 'text-right' },
        { key: 'p95_ms', label: 'p95', class: 'text-right' },
        { key: 'rows_served', label: 'Records', class: 'text-right' },
        { key: 'response_bytes', label: 'Data', class: 'text-right' },
        { key: 'actions', label: '', class: 'text-right' },
      ],
    }
  },
  computed: {
    /**
     * The API snaps the requested window back to the start of its bucket and returns
     * what it actually queried; showing that avoids a report labelled with a range it
     * did not use.
     */
    windowSummary() {
      if (!this.resolvedWindow) return this.preset.label

      const from = new Date(this.resolvedWindow.from)
      const to = new Date(this.resolvedWindow.to)

      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return this.preset.label

      return `${from.toLocaleString()} — ${to.toLocaleString()}`
    },
  },
  watch: {
    preset() {
      this.fetch()
    },
  },
  created() {
    this.fetch()
  },
  methods: {
    formatCount,
    formatBytes,
    formatPercent,
    formatDuration,
    errorRateVariant,
    statusBadge(status) {
      if (status === 'active') return 'success'
      if (status === 'suspended') return 'warning'
      return 'danger'
    },
    async fetch() {
      try {
        this.loading = true

        const request = await this.useJwt().mainService.apiClients.usageOverview(
          windowParams(this.preset),
        )
        const payload = get(request, 'data.data', {})

        this.totals = payload.totals || {}
        this.clients = payload.clients || []
        this.anomalies = payload.anomalies || []
        this.resolvedWindow = payload.window || null
      } catch (error) {
        const error_message = get(error, 'response.data.message') || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message,
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.usage-clients-table >>> td,
.usage-clients-table >>> th {
  padding: 0.6rem 1rem;
  font-size: 0.9rem;
}
</style>
